import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { caseHrefFor } from "../utils/caseHref";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Loader2,
  RefreshCw,
  Users,
  XCircle,
} from "lucide-react";
import {
  getBreakinCases,
  getBreakinReport,
  getBreakinReviewers,
} from "./breakinApi";
import { BREAKIN_STATUS_LABELS } from "./breakinData";
import { getStatusInfoFor, getClearlineStatusChip } from "../utils/statusMapping";
import { withinListRange } from "../utils/defaultDateRange";
import { useInfiniteScrollSentinel } from "../hooks/useInfiniteScrollSentinel";

/**
 * Hegic break-in.
 *
 * Three panels, each its own state, because they fail independently and one
 * missing panel should not take the page with it: the counts (all time,
 * unscoped — see getBreakinReport), the claims list, and who reviewed them.
 *
 * The claims list is filtered by the same date range as Reviewers, but not
 * the same way: /get_allreview_data takes start_date/end_date and the backend
 * honours them, while /assessment_list/all takes no date params at all —
 * probed directly, an identical request with and without start_date/end_date
 * comes back byte-for-byte the same. So `range` is applied client-side,
 * re-filtering every claim fetched SO FAR rather than just one page — the
 * list loads via infinite scroll (offset-based, 15 claims a page, same as
 * the backend always paged), accumulating pages as a reader scrolls, and the
 * date range narrows that accumulated set. Changing the range never
 * re-fetches anything: the backend ignores dates entirely, so there is
 * nothing a new request would get that a re-filter of what is already
 * loaded doesn't already have.
 */

const card = "rounded-card border border-cl-card bg-cl-surface";
const cardHead =
  "flex items-baseline justify-between gap-3 border-b border-cl-head px-4 py-[11px]";
const cardTitle = "text-[13.5px] font-semibold text-cl-text";
const hintClass = "text-[11px] text-cl-meta";

const FILL = "#2a78d6";
const GOOD = "#1d9a50";
const ALERT = "#d03b3b";
const AMBER = "#a37f12";

const num = (value) => (value || 0).toLocaleString("en-GB");
const pct = (value) =>
  value === null || value === undefined ? "—" : `${Math.round(value * 100)}%`;

const iso = (date) => date.toISOString().slice(0, 10);
const RANGE_DAYS = 30;

/** "2026-09-10 12:44:22" — not parsed; the format is not portable. */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const stamp = (value) => {
  if (typeof value !== "string" || value.length < 10) return "—";
  // Some backends send "YYYY-MM-DDTHH:MM:SS" instead of the space-separated
  // shape this was written for — normalize before splitting, same fix as
  // peopleList.js's formatModified, or the day and time both silently drop.
  const [date, time = ""] = value.replace("T", " ").split(" ");
  const [year, month, day] = date.split(/[-/]/);
  const name = MONTHS[Number(month) - 1];
  if (!name) return value;
  return `${Number(day)} ${name} ${year}${time ? `, ${time.slice(0, 5)}` : ""}`;
};

function Tile({ label, value, sub, icon: Icon, hue = FILL }) {
  return (
    <div
      className={`${card} relative overflow-hidden px-4 py-[13px] pl-[17px]`}
      style={{ background: `linear-gradient(180deg, ${hue}0f, ${hue}05)` }}
    >
      <span className="absolute bottom-0 left-0 top-0 w-[3px]" style={{ background: hue }} />
      <div className="flex items-center gap-[7px]">
        <span
          className="flex h-[20px] w-[20px] flex-none items-center justify-center rounded-[6px]"
          style={{ background: `${hue}1f`, color: hue }}
        >
          <Icon className="h-[12px] w-[12px]" />
        </span>
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">
          {label}
        </div>
      </div>
      <div className="mt-[5px] text-[23px] font-bold leading-none tracking-[-0.01em] text-cl-text">
        {value}
      </div>
      {sub && <div className="mt-[4px] text-[11.5px] text-cl-meta">{sub}</div>}
    </div>
  );
}

function Panel({ title, hint, children, error, onRetry, loading }) {
  return (
    <div className={card}>
      <div className={cardHead}>
        <span className={cardTitle}>{title}</span>
        {hint && <span className={hintClass}>{hint}</span>}
      </div>
      {loading ? (
        <div className="flex items-center gap-3 px-4 py-10 text-[12.5px] text-cl-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : error ? (
        <div className="px-4 py-8 text-center">
          <p className="text-[12.5px] text-cl-muted">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 inline-flex items-center gap-2 rounded-control border border-cl-control px-[13px] py-[6px] text-[12px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover"
          >
            <RefreshCw className="h-[12px] w-[12px]" />
            Try again
          </button>
        </div>
      ) : (
        children
      )}
    </div>
  );
}

const useLoader = (load) => {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const run = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    load()
      .then((data) => setState({ loading: false, error: null, data }))
      .catch((error) =>
        setState({
          loading: false,
          error:
            error?.response?.status === 401
              ? "This session is not accepted by the break-in backend."
              : error?.message || "Could not load",
          data: null,
        }),
      );
  }, [load]);
  useEffect(() => {
    run();
  }, [run]);
  return [state, run];
};

const PAGE = 15;

export default function BreakinPage() {
  const [range, setRange] = useState(() => {
    const to = new Date();
    const from = new Date(to.getTime() - RANGE_DAYS * 86400000);
    return { from: iso(from), to: iso(to) };
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);

  useEffect(() => {
    if (!showDatePicker) return undefined;
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDatePicker]);

  const loadReport = useCallback(() => getBreakinReport(), []);
  const loadReviewers = useCallback(
    () => getBreakinReviewers({ startDate: range.from, endDate: range.to }),
    [range.from, range.to],
  );

  const [report, retryReport] = useLoader(loadReport);
  const [reviewers, retryReviewers] = useLoader(loadReviewers);

  // Claims accumulate across pages instead of replacing one another — see
  // the file header comment for why that makes the date range usable at all
  // rather than only ever narrowing whatever single page offset 0 happens to
  // return. `cases` holds the latest page's own response (for list.total,
  // the lifetime count); `rows` is every claim fetched so far.
  const [rows, setRows] = useState([]);
  const [cases, setCases] = useState({ loading: true, error: null, data: null });
  const [loadingMore, setLoadingMore] = useState(false);
  // Separate from cases.error on purpose — a failed "load more" must not
  // replace the claims already on screen with the panel's full error view,
  // which cases.error does (see Panel below); it gets its own small,
  // inline, retryable notice instead.
  const [loadMoreError, setLoadMoreError] = useState(null);
  const scrollRef = useRef(null);

  const loadCases = useCallback((targetOffset, append) => {
    if (append) {
      setLoadingMore(true);
      setLoadMoreError(null);
    } else {
      setCases((s) => ({ ...s, loading: true, error: null }));
      setLoadMoreError(null);
    }

    getBreakinCases({ limit: PAGE, offset: targetOffset })
      .then((data) => {
        setCases({ loading: false, error: null, data });
        setRows((prev) => (append ? [...prev, ...data.rows] : data.rows));
      })
      .catch((error) => {
        const message =
          error?.response?.status === 401
            ? "This session is not accepted by the break-in backend."
            : error?.message || "Could not load";
        if (append) setLoadMoreError(message);
        else setCases({ loading: false, error: message, data: null });
      })
      .finally(() => {
        if (append) setLoadingMore(false);
      });
    // No deps: the request itself never varies with anything in this
    // component (the date range isn't a server parameter — see above), so
    // this never needs to change identity, and the mount effect below only
    // ever fires once.
  }, []);

  useEffect(() => {
    loadCases(0, false);
  }, [loadCases]);

  const hasMoreCases = Boolean(cases.data && cases.data.nextOffset !== null);

  const loadMoreCases = useCallback(() => {
    if (loadingMore || cases.loading || !hasMoreCases) return;
    loadCases(cases.data.nextOffset, true);
  }, [loadingMore, cases.loading, hasMoreCases, cases.data, loadCases]);

  const scrollSentinelRef = useInfiniteScrollSentinel({
    rootRef: scrollRef,
    enabled: hasMoreCases,
    onLoadMore: loadMoreCases,
  });

  const handleApplyRange = () => {
    setShowDatePicker(false);
  };

  const setRangeShortcut = (days) => {
    const to = new Date();
    const from = new Date(to.getTime() - days * 86400000);
    setRange({ from: iso(from), to: iso(to) });
  };

  const todayIso = iso(new Date());

  const r = report.data;
  const list = cases.data;
  // Narrows every claim loaded so far to the picked range — see the file
  // header comment for why this never needs another request to do that.
  const filteredRows = useMemo(
    () =>
      rows.filter((row) =>
        withinListRange(row.createdAt, {
          startDate: range.from,
          endDate: range.to,
        }),
      ),
    [rows, range.from, range.to],
  );
  const team = reviewers.data;

  const busiest = useMemo(
    () => (team?.rows || []).filter((row) => row.count > 0).slice(0, 8),
    [team],
  );

  return (
    <div className="cl-scroll h-full overflow-y-auto px-4 sm:px-8 pb-10 pt-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold tracking-[-0.01em] text-cl-text">
            Break-in
          </h1>
          <p className="mt-[2px] text-[12.5px] text-cl-muted">
            HDFC ERGO · a separate deployment from claims
          </p>
        </div>
        <div className="relative" ref={datePickerRef}>
          <button
            type="button"
            onClick={() => setShowDatePicker((shown) => !shown)}
            className="flex items-center gap-[7px] rounded-control border border-cl-control bg-cl-surface px-3 py-[9px] text-[12.5px] font-medium text-cl-secondary transition-colors hover:border-cl-faint"
          >
            <span>
              {range.from} → {range.to}
            </span>
            <ChevronDown className="h-[10px] w-[10px] text-cl-faint" />
          </button>
          {showDatePicker && (
            <div className="absolute right-0 top-[calc(100%+6px)] z-dropdown w-[300px] rounded-tile border border-cl-card bg-cl-surface p-4 shadow-[0_8px_24px_rgba(19,23,32,0.10)]">
              <div className="mb-3 text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                Quick select
              </div>
              <div className="mb-4 flex gap-2">
                {[
                  { days: 1, label: "Last day" },
                  { days: 7, label: "Last 7 days" },
                  { days: 30, label: "Last 30 days" },
                ].map((shortcut) => (
                  <button
                    key={shortcut.days}
                    type="button"
                    onClick={() => setRangeShortcut(shortcut.days)}
                    className="flex-1 rounded-tile border border-cl-control bg-cl-surface px-2 py-[6px] text-[11px] font-medium text-cl-secondary transition-colors hover:border-cl-accent hover:text-cl-accent"
                  >
                    {shortcut.label}
                  </button>
                ))}
              </div>

              <label className="mb-1 block text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                From
              </label>
              <input
                type="date"
                value={range.from}
                max={todayIso}
                onChange={(event) =>
                  setRange((current) => ({ ...current, from: event.target.value }))
                }
                className="cl-date-input mb-3 w-full rounded-tile border border-cl-control bg-cl-surface px-3 py-2 text-[12.5px] text-cl-text outline-none focus:border-cl-accent"
              />

              <label className="mb-1 block text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                To
              </label>
              <input
                type="date"
                value={range.to}
                min={range.from}
                max={todayIso}
                onChange={(event) =>
                  setRange((current) => ({ ...current, to: event.target.value }))
                }
                className="cl-date-input mb-4 w-full rounded-tile border border-cl-control bg-cl-surface px-3 py-2 text-[12.5px] text-cl-text outline-none focus:border-cl-accent"
              />

              <button
                type="button"
                onClick={handleApplyRange}
                className="w-full rounded-tile bg-cl-accent px-3 py-2 text-[12px] font-semibold text-white shadow-cl-primary transition-colors hover:bg-cl-accent-hover"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {report.loading ? (
        <div className="mt-5 flex items-center gap-3 py-10 text-[13px] text-cl-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Reading break-in totals…
        </div>
      ) : report.error ? (
        <div className={`${card} mt-5 px-4 py-[15px]`}>
          <div className="flex items-start gap-2">
            <AlertTriangle
              className="mt-[2px] h-[13px] w-[13px] flex-none"
              style={{ color: "var(--cl-warn-chip-fg)" }}
            />
            <p className="text-[12.5px] leading-[1.5] text-cl-secondary">{report.error}</p>
          </div>
          <button
            type="button"
            onClick={retryReport}
            className="mt-3 rounded-control border border-cl-control px-[13px] py-[6px] text-[12px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover"
          >
            Try again
          </button>
        </div>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
            <Tile label="Claims" value={num(r.total)} sub="all time" icon={ClipboardList} />
            <Tile
              label="Completed"
              value={num(r.completed)}
              sub={`${pct(r.completedRate)} of all claims`}
              icon={CheckCircle2}
              hue={GOOD}
            />
            <Tile
              label="In review"
              value={num(r.review)}
              sub="waiting on a reviewer"
              icon={Users}
              hue={AMBER}
            />
            <Tile
              label="Failed"
              value={num(r.failed)}
              sub={`${pct(r.failedRate)} of all claims`}
              icon={XCircle}
              hue={ALERT}
            />
            <Tile label="Updated" value={num(r.updated)} sub="touched after capture" icon={RefreshCw} />
          </div>

          {/* The parts overshoot the whole, so say so rather than letting a
              reader add them up and distrust the page. */}
          <p className="mt-3 text-[11.5px] text-cl-meta">
            These are the backend's own counters, not a breakdown: a claim can
            be both updated and completed, so the four add up to more than the
            total.
          </p>
        </>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel
          title="Recent claims"
          hint={list ? `${num(list.total)} lifetime · ${num(rows.length)} loaded` : null}
          loading={cases.loading}
          error={cases.error}
          onRetry={() => loadCases(0, false)}
        >
          <div ref={scrollRef} className="cl-scroll max-h-[420px] overflow-x-auto overflow-y-auto">
            <table className="w-full min-w-[560px] border-collapse text-[12.5px]">
              <thead>
                <tr>
                  {["Reference", "Status", "Review", "Captured"].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      // z-[1], not z-10: the date picker above uses the
                      // z-dropdown token, which this project's scale
                      // (design-tokens.css) sets to the same 10 — so the
                      // open popover rendered behind this header instead
                      // of over it. Only needs to clear the unstyled rows
                      // scrolling underneath, not outrank a page-level
                      // dropdown.
                      className="sticky top-0 z-[1] bg-cl-head px-3 py-[8px] text-left text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-[12.5px] text-cl-muted">
                      {hasMoreCases
                        ? "None of the claims loaded so far are in the selected range — scroll down to load more."
                        : "No claims are in the selected range."}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => {
                    // Break-in's own wording: 3 is Completed here, not
                    // "AI Assessed", and 8 is Accepted, not "Review
                    // Completed".
                    const status = getStatusInfoFor(
                      row.claimState,
                      BREAKIN_STATUS_LABELS,
                    );
                    const chip = getClearlineStatusChip(status.text);
                    const review = getStatusInfoFor(
                      row.reviewState,
                      BREAKIN_STATUS_LABELS,
                    );
                    return (
                      <tr key={row.id} className="border-t border-cl-row transition-colors hover:bg-cl-hover">
                        <td className="px-3 py-[9px] font-medium">
                          {/* target="_blank" so a plain click opens a new tab
                              too, same as every other case list in the app.
                              No router state reaches that tab, so the detail
                              page's Back button correctly hides itself (see
                              src/utils/backNavigation.js). */}
                          <Link
                            to={caseHrefFor(row.refNum, "/redesign/breakin")}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cl-text transition-colors hover:text-cl-accent"
                          >
                            {row.refNum}
                          </Link>
                        </td>
                        <td className="px-3 py-[9px]">
                          <span
                            className="inline-flex items-center rounded-full px-[8px] py-[3px] text-[11.5px] font-semibold"
                            style={{ background: chip.bg, color: chip.fg }}
                          >
                            {status.text}
                          </span>
                        </td>
                        <td className="px-3 py-[9px] text-cl-secondary">{review.text}</td>
                        <td className="px-3 py-[9px] whitespace-nowrap text-cl-muted">
                          {stamp(row.createdAt)}
                        </td>
                      </tr>
                    );
                  })
                )}
                {/* Always rendered while there's more to load — not just
                    while actually loading — because it IS the trigger: the
                    observer watches this row, so it has to already be on
                    screen (inside scrollRef) before a reader scrolls near
                    it, not only appear once loading has already started.
                    Kept even when filteredRows is empty, so a narrow date
                    range can still auto-load further pages looking for a
                    match instead of stalling with nothing left to scroll. */}
                <tr ref={scrollSentinelRef}>
                  <td colSpan={4} className="px-3 py-[14px] text-center text-[11.5px] text-cl-meta">
                    {loadingMore ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Loading more…
                      </span>
                    ) : loadMoreError ? (
                      <span className="inline-flex items-center gap-2">
                        {loadMoreError}
                        <button
                          type="button"
                          onClick={() => loadCases(cases.data.nextOffset, true)}
                          className="font-semibold text-cl-accent transition-colors hover:text-cl-accent-hover"
                        >
                          Retry
                        </button>
                      </span>
                    ) : !hasMoreCases ? (
                      "You've reached the end of the list"
                    ) : null}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel
          title="Reviewers"
          hint={team ? `${team.people} people · ${range.from} to ${range.to}` : null}
          loading={reviewers.loading}
          error={reviewers.error}
          onRetry={retryReviewers}
        >
          {team && team.reviewed === 0 ? (
            <p className="px-4 py-8 text-center text-[12.5px] text-cl-muted">
              Nobody reviewed a break-in claim in this period. All{" "}
              {team.people} people are still listed — widen the dates to see
              who has been working.
            </p>
          ) : (
            <div className="py-[6px]">
              {busiest.map((person) => (
                <div
                  key={person.email}
                  className="flex items-center gap-3 px-4 py-[7px]"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12.5px] font-medium text-cl-text">
                      {person.name || person.email}
                    </div>
                    <div className="truncate text-[11px] text-cl-faint">
                      {person.role}
                    </div>
                  </div>
                  <div className="h-[9px] w-[38%] flex-none overflow-hidden rounded-[5px]" style={{ background: `${FILL}1a` }}>
                    <div
                      className="h-full rounded-[5px]"
                      style={{
                        width: `${Math.max(2, (person.count / busiest[0].count) * 100)}%`,
                        background: FILL,
                      }}
                    />
                  </div>
                  <div className="w-[46px] flex-none text-right text-[12.5px] font-bold text-cl-secondary">
                    {num(person.count)}
                  </div>
                </div>
              ))}
              {team && (
                <p className="border-t border-cl-row px-4 py-[10px] text-[11.5px] text-cl-meta">
                  {team.active} of {team.people} reviewed anything in this
                  period, {num(team.reviewed)} claims between them.
                </p>
              )}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
